import React from "react";

import Debug from "./Routes/Debug";
import Landing from "./Routes/Landing";
import Profile from "./Routes/Profile/Profile";
import Quizzer from "./Routes/Quizzer/Quizzer";
import SubjectsIndex from "./Routes/SubjectIndex/SubjectIndex";
import SubjectDetail from "./Routes/Subjects/SubjectDetail/Container";
import SubjectLanding from "./Routes/Subjects/SubjectLanding/Container";
import SubjectSecret from "./Routes/SubjectSecret";
import SubjectSelector from "./Routes/SubjectSelector/SubjectSelector";
import SubjectSettings from "./Routes/SubjectSettings";
import SubjectSimpleEdit from "./Routes/SubjectSimpleEdit";
import SubjectStats from "./Routes/SubjectStats";
import SubjectStatsAtoms from "./Routes/SubjectStatsAtoms";

import QuestionList from "./Routes/QuestionList";
import QuizObject from "./Routes/QuizObject";
import QuizObjectEditorJs from "./Routes/QuizObjectEditorJs";

import Forgot from "./Passport/Forgot";
import Login from "./Passport/Login/LoginContainer";
import Reset from "./Passport/Reset";
import Signup from "./Passport/Signup/SignupContainer";
import FinishSocialSignup from "./Passport/FinishSocialSignup/FinishSocialSignupContainer";

const PREFIX = '/modquiz';

// Single source of truth: path template, rendered element, and path builder together.
// modquizRoutes and modquizPaths are both derived from this object.
const routeDefs = {
  landing: {
    path: '',
    element: <Landing />,
    to: () => PREFIX,
  },
  subjects: {
    path: 'subjects',
    element: <SubjectsIndex />,
    to: () => `${PREFIX}/subjects`,
  },
  subject: {
    path: 'subjects/:user/:subject',
    element: <SubjectDetail />,
    to: (hashKey: string) => `${PREFIX}/subjects/${hashKey}`,
  },
  subjectLanding: {
    path: 'subjects/:user/:subject/landing',
    element: <SubjectLanding />,
    to: (hashKey: string) => `${PREFIX}/subjects/${hashKey}/landing`,
  },
  subjectEdit: {
    path: 'subjects/:user/:subject/edit',
    element: <SubjectDetail />,
    to: (hashKey: string) => `${PREFIX}/subjects/${hashKey}/edit`,
  },
  subjectSimple: {
    path: 'subjects/:user/:subject/simple',
    element: <SubjectSimpleEdit />,
    to: (hashKey: string) => `${PREFIX}/subjects/${hashKey}/simple`,
  },
  subjectSettings: {
    path: 'subjects/:user/:subject/settings',
    element: <SubjectSettings />,
    to: (hashKey: string) => `${PREFIX}/subjects/${hashKey}/settings`,
  },
  subjectStats: {
    path: 'subjects/:user/:subject/stats',
    element: <SubjectStats />,
    to: (hashKey: string) => `${PREFIX}/subjects/${hashKey}/stats`,
  },
  subjectStatsAtoms: {
    path: 'subjects/:user/:subject/stats_atoms',
    element: <SubjectStatsAtoms />,
    to: (hashKey: string) => `${PREFIX}/subjects/${hashKey}/stats_atoms`,
  },
  subjectSecret: {
    path: 'subjects/:user/:subject/secret',
    element: <SubjectSecret />,
    to: (hashKey: string) => `${PREFIX}/subjects/${hashKey}/secret`,
  },
  quizObject: {
    path: 'subjects/:subject_user/:subject_slug/o/:object_id',
    element: <QuizObjectEditorJs />,
    to: (hashKey: string, objectId: string) => `${PREFIX}/subjects/${hashKey}/o/${objectId}`,
  },
  quizObjectNew: {
    path: 'subjects/:subject_user/:subject_slug/o/:object_id/new',
    element: <QuizObjectEditorJs />,
    to: (hashKey: string, objectId: string) => `${PREFIX}/subjects/${hashKey}/o/${objectId}/new`,
  },
  newQuizObject: {
    path: 'subjects/:subject_user/:subject_slug/o/new',
    element: <QuizObjectEditorJs />,
    to: (hashKey: string) => `${PREFIX}/subjects/${hashKey}/o/new`,
  },
  quiz: {
    path: 'quiz',
    element: <SubjectSelector />,
    to: () => `${PREFIX}/quiz`,
  },
  quizSubject: {
    path: 'quiz/:subject_user/:subject_slug',
    element: <Quizzer />,
    to: (hashKey: string) => `${PREFIX}/quiz/${hashKey}`,
  },
  profile: {
    path: 'profile',
    element: <Profile />,
    to: () => `${PREFIX}/profile`,
  },
  questionList: {
    path: 'question_list/:subject_user/:subject_slug',
    element: <QuestionList />,
    to: (hashKey: string) => `${PREFIX}/question_list/${hashKey}`,
  },
  debug: {
    path: 'debug/quts/:quts_id',
    element: <Debug />,
    to: (id: string) => `${PREFIX}/debug/quts/${id}`,
  },
};

export const modquizPaths = Object.fromEntries(
  Object.entries(routeDefs).map(([key, { to }]) => [key, to])
) as { [K in keyof typeof routeDefs]: typeof routeDefs[K]['to'] };

export const modquizRoutes = [
  ...Object.values(routeDefs).map(({ path, element }) => ({ path, element })),

  // Auth
  { path: 'login',                element: <Login /> },
  { path: 'reset/:token',         element: <Reset /> },
  { path: 'finish-social-signup', element: <FinishSocialSignup /> },
  { path: 'signup',               element: <Signup /> },
  { path: 'forgot',               element: <Forgot /> },

  // Legacy "subject" (singular) aliases
  { path: 'subject',                                               element: <SubjectsIndex /> },
  { path: 'subject/:subject',                                      element: <Profile /> },
  { path: 'subject/:user/:subject',                                element: <SubjectDetail /> },
  { path: 'subject/:user/:subject/landing',                        element: <SubjectLanding /> },
  { path: 'subject/:user/:subject/edit',                           element: <SubjectDetail /> },
  { path: 'subject/:user/:subject/simple_edit',                    element: <SubjectSimpleEdit /> },
  { path: 'subject/:user/:subject/simple_editor',                  element: <SubjectSimpleEdit /> },
  { path: 'subject/:user/:subject/simple',                         element: <SubjectSimpleEdit /> },
  { path: 'subject/:user/:subject/settings',                       element: <SubjectSettings /> },
  { path: 'subject/:user/:subject/stats',                          element: <SubjectStats /> },
  { path: 'subject/:user/:subject/stats_atoms',                    element: <SubjectStatsAtoms /> },
  { path: 'subject/:user/:subject/secret',                         element: <SubjectSecret /> },
  { path: 'subject/:subject_user/:subject_slug/o/:object_id',      element: <QuizObject /> },
  { path: 'subject/:subject_user/:subject_slug/o/:object_id/new',  element: <QuizObjectEditorJs /> },

  // Other "subjects" (plural) aliases not in routeDefs
  { path: 'subjects/:subject',                     element: <Profile /> },
  { path: 'subjects/:user/:subject/simple_edit',   element: <SubjectSimpleEdit /> },
  { path: 'subjects/:user/:subject/simple_editor', element: <SubjectSimpleEdit /> },

  // Short object routes
  { path: 'object/:object_id',      element: <QuizObject /> },
  { path: 'objects/:object_id',     element: <QuizObject /> },
  { path: 'object/:object_id/new',  element: <QuizObjectEditorJs /> },
  { path: 'objects/:object_id/new', element: <QuizObjectEditorJs /> },

  // Quiz aliases
  { path: 'quiz/:subject_user/:subject_slug/:object_id',          element: <Quizzer /> },
  { path: 'quiz/:subject',                                        element: <Quizzer /> },
  { path: 'quiz/subjects/:user/:subject',                         element: <Profile /> },
  { path: 'quiz/subjects/:user/:subject/category/:category',      element: <Profile /> },
  { path: 'quiz/subjects/:subject',                               element: <Profile /> },
  { path: 'quiz/subjects/:subject/category/:category',            element: <Profile /> },
  { path: 'quiz/subject/add',                                     element: <Profile /> },

  // Profile aliases
  { path: 'profile/subjects/:user/:subject',    element: <SubjectDetail /> },
  { path: 'profile/:profile_id/subjects/:subject', element: <Profile /> },
  { path: 'profile/subject/add',               element: <Profile /> },

  { path: 'subscribe', element: <Profile /> },
];

export { Landing };
